import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { slugify, BLOG_ANSWER_KEYS, type Translatable } from '@hrlabs/shared-types';
import { Timestamp, deleteField } from 'firebase/firestore';
import { getRowBy, listCollection, saveRow } from '../../lib/db';
import { ArrayField } from '../../components/ArrayField';
import { uploadToStorage, storageUrlFromPath } from '../../lib/upload';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../lib/auth';
import { BilingualField } from '../../components/BilingualField';
import { translateBlogPost } from '../../lib/functions';
import { INTERVIEW_QUESTIONS, plainQuestion } from '../../lib/interviewQuestions';

const EMPTY: Translatable = { en: '', bg: '' };
type Answers = Record<(typeof BLOG_ANSWER_KEYS)[number], Translatable>;
const emptyAnswers = (): Answers =>
  Object.fromEntries(BLOG_ANSWER_KEYS.map((k) => [k, { ...EMPTY }])) as Answers;

export function BlogEdit() {
  const { slug } = useParams();
  const isNew = !slug || slug === 'new';
  const navigate = useNavigate();
  const { notify } = useToast();
  const { user } = useAuth();

  const [docId, setDocId] = useState<string | undefined>();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [openAnswers, setOpenAnswers] = useState(false);
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [portraitFile, setPortraitFile] = useState<File | null>(null);
  const [portraitPath, setPortraitPath] = useState<string | null>(null);
  const [portraitPreview, setPortraitPreview] = useState<string | null>(null);
  const [portraitError, setPortraitError] = useState('');
  const [focus, setFocus] = useState({ x: 50, y: 30 });

  const [title, setTitle] = useState<Translatable>({ ...EMPTY });
  const [subtitle, setSubtitle] = useState<Translatable>({ ...EMPTY });
  const [description, setDescription] = useState<Translatable>({ ...EMPTY });
  const [answers, setAnswers] = useState<Answers>(emptyAnswers());
  const [status, setStatus] = useState('draft');
  const [thumbnailPath, setThumbnailPath] = useState<string | null>(null);
  const [summary, setSummary] = useState<Translatable>({ ...EMPTY });
  const [role, setRole] = useState<Translatable>({ ...EMPTY });
  const [anonymised, setAnonymised] = useState(false);
  const [needsReview, setNeedsReview] = useState(false);
  const [topics, setTopics] = useState<Translatable[]>([]);
  const [readMinutes, setReadMinutes] = useState('');
  const [authorId, setAuthorId] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [publishedOn, setPublishedOn] = useState('');
  const [origPublishedOn, setOrigPublishedOn] = useState('');
  const [teamNames, setTeamNames] = useState<string[]>([]);

  useEffect(() => {
    listCollection('teamMembers').then((rows) =>
      setTeamNames(
        rows
          .map((r) => {
            const f = (r.firstName ?? {}) as { en?: string };
            const l = (r.lastName ?? {}) as { en?: string };
            return `${f.en ?? ''} ${l.en ?? ''}`.trim();
          })
          .filter(Boolean),
      ),
    );
  }, []);

  useEffect(() => {
    if (isNew) return;
    getRowBy('blogPosts', 'slug', slug!).then((r) => {
      if (r) {
        setDocId(r.id);
        setTitle((r.title as Translatable) ?? { ...EMPTY });
        setSubtitle((r.subtitle as Translatable) ?? { ...EMPTY });
        setDescription((r.description as Translatable) ?? { ...EMPTY });
        const a = emptyAnswers();
        for (const k of BLOG_ANSWER_KEYS) a[k] = (r[k] as Translatable) ?? { ...EMPTY };
        setAnswers(a);
        setStatus(String(r.status ?? 'draft'));
        setThumbnailPath((r.thumbnailPath as string | null) ?? null);
        setPortraitPath((r.portraitPath as string | null) ?? null);
        const f = r.portraitFocus as { x?: number; y?: number } | undefined;
        if (f && typeof f.x === 'number' && typeof f.y === 'number') setFocus({ x: f.x, y: f.y });
        setSummary((r.answer as Translatable) ?? { ...EMPTY });
        setRole((r.role as Translatable) ?? { ...EMPTY });
        setAnonymised(Boolean(r.anonymised));
        setNeedsReview(Boolean(r.needsReview));
        setTopics(Array.isArray(r.topics) ? (r.topics as Translatable[]) : []);
        setReadMinutes(typeof r.readMinutes === 'number' ? String(r.readMinutes) : '');
        setAuthorId(String(r.authorId ?? ''));
        setAuthorName(String(r.authorName ?? ''));
        setSlugInput(String(r.slug ?? ''));
        const c = r.createdAt as { toDate?: () => Date } | undefined;
        const d = c?.toDate ? c.toDate().toISOString().slice(0, 10) : '';
        setPublishedOn(d);
        setOrigPublishedOn(d);
      }
      setLoading(false);
    });
  }, [isNew, slug]);

  const autoSlug = useMemo(() => slugify(title.en || title.bg || ''), [title]);
  // Existing posts keep their slug (changing a title never changes the URL); new posts follow the title until typed over.
  const shownSlug = isNew && !slugTouched ? autoSlug : slugInput;

  const runTranslate = async () => {
    setTranslating(true);
    try {
      const payload: Record<string, string> = { description_bg: description.bg };
      for (const k of BLOG_ANSWER_KEYS) if (answers[k].bg.trim()) payload[`${k}_bg`] = answers[k].bg;
      const res = await translateBlogPost(payload as never);
      const data = res.data.data as Record<string, string>;
      if (data.description) setDescription((d) => ({ ...d, en: data.description }));
      setAnswers((prev) => {
        const next = { ...prev };
        for (const k of BLOG_ANSWER_KEYS) if (data[k]) next[k] = { ...next[k], en: data[k]! };
        return next;
      });
      notify('success', 'Translated the English fields.');
    } catch (err) {
      notify('danger', `Translate failed: ${(err as Error).message}`);
    } finally {
      setTranslating(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.bg.trim() || !description.bg.trim()) {
      notify('danger', 'Bulgarian title and description are required.');
      return;
    }
    setSaving(true);
    try {
      const newSlug = slugify(shownSlug) || autoSlug || docId || `post-${Date.now()}`;
      const clash = await getRowBy('blogPosts', 'slug', newSlug);
      if (clash && clash.id !== docId) {
        notify('danger', `The slug "${newSlug}" is already used by another interview.`);
        setSaving(false);
        return;
      }
      const id = await saveRow('blogPosts', docId, {
        title,
        subtitle,
        description,
        slug: newSlug,
        status,
        authorId: authorId || user?.uid || '',
        authorName: authorName.trim() || user?.displayName || user?.email || 'HRLabs',
        ...(publishedOn && publishedOn !== origPublishedOn
          ? { createdAt: Timestamp.fromDate(new Date(`${publishedOn}T12:00:00`)) }
          : {}),
        ...answers,
        answer: summary.en.trim() || summary.bg.trim() ? summary : deleteField(),
        role: role.en.trim() || role.bg.trim() ? role : deleteField(),
        anonymised,
        needsReview,
        topics: topics.filter((t) => t.en.trim() || t.bg.trim()),
        readMinutes: readMinutes ? Number(readMinutes) : deleteField(),
      });
      if (portraitFile) {
        const { path } = await uploadToStorage('blog-thumbnails', id, portraitFile);
        await saveRow('blogPosts', id, { portraitPath: path, portraitFocus: focus });
        setPortraitPath(path);
      } else if (portraitPath) {
        await saveRow('blogPosts', id, { portraitFocus: focus });
      } else {
        await saveRow('blogPosts', id, { portraitPath: deleteField(), portraitFocus: deleteField() });
      }
      if (thumbFile) {
        const { path } = await uploadToStorage('blog-thumbnails', id, thumbFile);
        await saveRow('blogPosts', id, { thumbnailPath: path });
        setThumbnailPath(path);
      }
      notify('success', 'Post saved.');
      navigate(`/interviews/${newSlug}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onPortraitPick = (file: File | null) => {
    setPortraitError('');
    if (portraitPreview) URL.revokeObjectURL(portraitPreview);
    if (!file) {
      setPortraitFile(null);
      setPortraitPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      if (img.naturalWidth < 480 || img.naturalHeight < 600) {
        setPortraitError(`Too small: ${img.naturalWidth}×${img.naturalHeight}. Minimum is 480×600.`);
      } else if (Math.abs(ratio / 0.8 - 1) > 0.05) {
        setPortraitError(
          `Wrong shape: ${img.naturalWidth}×${img.naturalHeight}. Portraits must be 4:5 (e.g. 480×600, 800×1000).`,
        );
      } else {
        setPortraitFile(file);
        setPortraitPreview(url);
        return;
      }
      URL.revokeObjectURL(url);
      setPortraitFile(null);
      setPortraitPreview(null);
    };
    img.onerror = () => {
      setPortraitError('Could not read that image.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const portraitSrc = portraitPreview ?? storageUrlFromPath(portraitPath);
  const fit = { objectFit: 'cover', objectPosition: `${focus.x}% ${focus.y}%` } as const;

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="fw-bold m-0">{isNew ? 'New interview story' : 'Edit interview story'}</h3>
        <button
          type="button"
          className="btn btn-outline-success btn-sm"
          onClick={runTranslate}
          disabled={translating || !description.bg.trim()}
        >
          {translating ? 'Translating…' : 'Translate with AI'}
        </button>
      </div>

      <BilingualField label="Title" value={title} onChange={setTitle} required rows={1} />
      <BilingualField label="Subtitle (not shown on the site)" value={subtitle} onChange={setSubtitle} rows={1} />
      <BilingualField label="Description" value={description} onChange={setDescription} required rich />

      <div className="mb-3">
        <button
          type="button"
          className="btn btn-sm btn-light border"
          onClick={() => setOpenAnswers((o) => !o)}
        >
          {openAnswers ? '▾' : '▸'} Interview answers (1–13)
        </button>
        {openAnswers && (
          <div className="mt-3 ps-2 border-start">
            {BLOG_ANSWER_KEYS.map((k, i) => (
              <div key={k}>
                <div className="alert alert-secondary py-2 mb-2 small">
                  <strong>Question {i + 1}</strong>
                  <div>
                    <span className="text-uppercase text-muted me-1">bg</span>
                    {plainQuestion(INTERVIEW_QUESTIONS[i]?.bg ?? '')}
                  </div>
                  <div>
                    <span className="text-uppercase text-muted me-1">en</span>
                    {plainQuestion(INTERVIEW_QUESTIONS[i]?.en ?? '')}
                  </div>
                </div>
                <BilingualField
                  label={`Answer ${i + 1}`}
                  value={answers[k]}
                  onChange={(v) => setAnswers((a) => ({ ...a, [k]: v }))}
                  rich
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <BilingualField label="Answer-first summary (40–80 words; shown at the top and in the listing. Empty = start of the description)" value={summary} onChange={setSummary} rows={3} />

      <section className="border rounded p-3 p-md-4 mb-4 bg-white">
        <h2 className="fs-6 fw-bold mb-3 pb-2 border-bottom">Byline and review</h2>
        <div className="form-check mb-3">
          <input className="form-check-input" type="checkbox" id="anon" checked={anonymised} onChange={(e) => setAnonymised(e.target.checked)} />
          <label className="form-check-label" htmlFor="anon">
            Anonymised account — the byline reads &quot;Anonymised account · {'{'}role{'}'}&quot; instead of naming the author
          </label>
        </div>
        <BilingualField
          label={anonymised ? 'Role shown in the byline (e.g. Senior backend engineer)' : "Interviewee's role, shown under their name (optional)"}
          value={role}
          onChange={setRole}
          rows={1}
        />
        <div className="form-check mb-3">
          <input className="form-check-input" type="checkbox" id="review" checked={needsReview} onChange={(e) => setNeedsReview(e.target.checked)} />
          <label className="form-check-label" htmlFor="review">
            Needs legal / consent review (the story names real employers). Internal flag — not shown on the site.
          </label>
        </div>
        <div style={{ maxWidth: 260 }}>
          <label className="form-label fw-semibold text-nowrap">Reading time (min, optional)</label>
          <input type="number" min={1} className="form-control" value={readMinutes} onChange={(e) => setReadMinutes(e.target.value)} />
        </div>
      </section>

      <ArrayField label="Topics" items={topics} onChange={setTopics} blank={() => ({ ...EMPTY })} addLabel="Add topic"
        render={(t, set) => <BilingualField label="Topic" value={t} onChange={set} rows={1} />} />

      <div className="mb-3" style={{ maxWidth: 260 }}>
        <label className="form-label fw-semibold">Status</label>
        <select
          className="form-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="draft">draft</option>
          <option value="published">published</option>
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Thumbnail</label>
        <input
          type="file"
          className="form-control"
          accept="image/*"
          onChange={(e) => setThumbFile(e.target.files?.[0] ?? null)}
        />
        {thumbnailPath && <div className="small text-muted mt-1">current: {thumbnailPath}</div>}
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Portrait (4:5)</label>
        <input
          type="file"
          className="form-control"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => onPortraitPick(e.target.files?.[0] ?? null)}
        />
        <div className="form-text">
          Professional headshot, 4:5, at least 480×600. Keep the crop consistent with the other
          stories; if it cannot match, leave it empty and initials are shown instead. The
          thumbnail above is only used for social sharing.
        </div>
        {portraitError && <div className="text-danger small mt-1">{portraitError}</div>}
        {portraitSrc && (
          <div className="d-flex align-items-start gap-4 mt-2 flex-wrap">
            <img src={portraitSrc} alt="Portrait preview" width={120} height={150} style={{ borderRadius: 12, ...fit }} />
            <img src={portraitSrc} alt="" width={56} height={56} style={{ borderRadius: '50%', ...fit }} />
            <div style={{ minWidth: 220 }}>
              <label className="form-label small mb-0">Focus X: {focus.x}%</label>
              <input type="range" className="form-range" min={0} max={100} value={focus.x}
                onChange={(e) => setFocus({ ...focus, x: Number(e.target.value) })} />
              <label className="form-label small mb-0">Focus Y: {focus.y}%</label>
              <input type="range" className="form-range" min={0} max={100} value={focus.y}
                onChange={(e) => setFocus({ ...focus, y: Number(e.target.value) })} />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm mt-1"
                onClick={() => {
                  onPortraitPick(null);
                  setPortraitPath(null);
                }}
              >
                Remove portrait
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">URL slug</label>
          <input
            className="form-control"
            value={shownSlug}
            placeholder="(from English title)"
            onChange={(e) => {
              setSlugTouched(true);
              setSlugInput(e.target.value);
            }}
          />
          <div className="form-text">
            {isNew
              ? 'Follows the English title until you edit it.'
              : 'Changing this changes the public URL; the old URL stops working.'}
          </div>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold">Published on</label>
          <input
            type="date"
            className="form-control"
            value={publishedOn}
            onChange={(e) => setPublishedOn(e.target.value)}
          />
          <div className="form-text">Shown in the byline and feeds. Empty = today.</div>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold">Author</label>
          <input
            className="form-control"
            list="team-names"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
          />
          <datalist id="team-names">
            {teamNames.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          <div className="form-text">HRLabs writer shown in the byline.</div>
        </div>
      </div>

      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
